import { useState, useEffect, Fragment } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { RagChip } from "@/components/RagChip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Download, Search, ChevronLeft, ChevronRight, Loader2, Sparkles, Info,
  ChevronDown, ChevronUp, History, FolderKanban, Percent, CircleCheck, TriangleAlert,
  CircleX, CalendarClock, Ban, LifeBuoy, X, Clock, ArrowUp, ArrowDown, ArrowRight, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const HEALTH_KPIS = [
  { key: "on_track", health: "green", title: "Green", icon: CircleCheck, color: "#107C41" },
  { key: "at_risk", health: "amber", title: "Amber", icon: TriangleAlert, color: "#D97706" },
  { key: "behind", health: "red", title: "Red", icon: CircleX, color: "#A80000" },
];

export default function PortfolioOverview() {
  const { session, asOf } = useSession();
  const isAdmin = session?.role === "pmo_admin";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("all");
  const [health, setHealth] = useState("all");
  const [sort, setSort] = useState("project_name");
  const [direction, setDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expanded, setExpanded] = useState(null);
  const [ovTarget, setOvTarget] = useState(null);
  const [ovRag, setOvRag] = useState("amber");
  const [ovReason, setOvReason] = useState("");
  const [ovSaving, setOvSaving] = useState(false);

  const { data: filters } = useQuery({
    queryKey: ["filters", session?.persona],
    queryFn: async () => (await api.get("/filters")).data,
  });

  useEffect(() => { setPage(1); }, [asOf]);

  const params = { as_of: asOf, search, platform, health, sort, direction, page, page_size: pageSize };
  const { data, isLoading } = useQuery({
    queryKey: ["portfolio", session?.persona, params],
    queryFn: async () => (await api.get("/portfolio", { params })).data,
  });

  const toggleSort = (key) => {
    if (sort === key) setDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setDirection("asc"); }
    setPage(1);
  };
  const toggleHealth = (h) => { setHealth((cur) => (cur === h ? "all" : h)); setPage(1); };
  const clearFilters = () => { setSearch(""); setPlatform("all"); setHealth("all"); setPage(1); };

  const openOverride = (row) => { setOvTarget(row); setOvRag("amber"); setOvReason(""); };
  const submitOverride = async () => {
    if (!ovReason.trim()) { toast.error("A justification reason is required."); return; }
    setOvSaving(true);
    try {
      await api.post(`/projects/${ovTarget.project_id}/override`, {
        rag: ovRag, reason: ovReason.trim(), month_key: ovTarget.month_key,
      });
      toast.success("Health override applied");
      setOvTarget(null);
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    } finally { setOvSaving(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/portfolio/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "pmo_portfolio_overview.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Portfolio exported");
    } catch { toast.error("Export failed"); }
  };

  const k = data?.kpis || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const staticKpis = [
    { title: "Projects in view", value: k.total_projects, icon: FolderKanban, color: "#0078D4", tid: "kpi-card-total-projects", bg: "#FFFFFF" },
    { title: "Average completion", value: k.avg_completion, suffix: "%", icon: Percent, color: "#008272", tid: "kpi-card-avg-completion", bg: "#FFFFFF" },
    { title: "Overdue projects", value: k.overdue, icon: CalendarClock, color: "#8764B8", tid: "kpi-card-overdue", bg: "#FFFFFF" },
    { title: "With blockers", value: k.blocked, icon: Ban, color: "#605E5C", tid: "kpi-card-blocked", bg: "#FFFFFF" },
    { title: "Needing support", value: k.support_required, icon: LifeBuoy, color: "#005A9E", tid: "kpi-card-support", bg: "#FFFFFF" },
  ];

  return (
    <div className="space-y-5 fluent-enter" data-testid="portfolio-overview-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#201F1E]">Portfolio Overview</h1>
          <p className="text-sm text-[#605E5C] mt-1">Latest published update per project, with monthly history.</p>
        </div>
        <Button onClick={exportCsv} data-testid="export-csv-button" className="bg-[#0078D4] hover:bg-[#005A9E] text-white h-9 text-sm fluent-focus">
          <Download size={15} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Shared controls */}
      <div className="fluent-card p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-2.5 top-2.5 text-[#605E5C]" />
          <Input data-testid="portfolio-search" placeholder="Search project, contributor, tech…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 h-9 text-sm fluent-focus" />
        </div>
        <Select value={platform} onValueChange={(v) => { setPlatform(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[150px] text-sm fluent-focus" data-testid="filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            {(filters?.platforms || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={health} onValueChange={(v) => { setHealth(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[150px] text-sm fluent-focus" data-testid="filter-health"><SelectValue placeholder="Project Health" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Health</SelectItem>
            <SelectItem value="green">Green (On Track)</SelectItem>
            <SelectItem value="amber">Amber (At Risk)</SelectItem>
            <SelectItem value="red">Red (Behind)</SelectItem>
            <SelectItem value="not_rated">Not Rated</SelectItem>
          </SelectContent>
        </Select>
        {(search || platform !== "all" || health !== "all") && (
          <Button variant="outline" onClick={clearFilters} data-testid="clear-filters" className="h-9 text-xs fluent-focus">
            <X size={13} className="mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {isLoading || !data ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="animate-spin text-[#0078D4]" size={26} /></div>
      ) : (
        <>
          {/* KPI cards */}
          {/* Row 1: Projects in View, Green, Amber, Red */}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
            <StaticCard c={staticKpis[0]} />
            {HEALTH_KPIS.map((c) => (
              <HealthCard key={c.key} c={c} value={k[c.key] ?? 0} selected={health === c.health} onClick={() => toggleHealth(c.health)} />
            ))}
          </div>
          {k.not_rated > 0 && (
            <div className="text-xs text-[#605E5C]" data-testid="not-rated-note">
              {k.not_rated} project(s) Not Rated (insufficient schedule data) — not counted as Green.
            </div>
          )}

          {/* Executive brief */}
          <div className="fluent-card p-5" data-testid="executive-brief">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#008272]" />
              <h2 className="text-base font-semibold text-[#201F1E]">Executive Brief</h2>
            </div>
          </div>

          {/* Freshness notice */}
          {data.carried_forward_count > 0 && (
            <div className="rounded border border-[#FFE8A3] bg-[#FFF9E6] px-4 py-2.5 text-xs text-[#8A6D00] flex items-center gap-2" data-testid="freshness-notice">
              <Clock size={14} /> {data.carried_forward_count} project(s) have no update in {data.is_latest ? "the latest reporting month" : data.cutoff_label} — showing their most recent prior submission (carried forward). Missing submissions do not change Project Health.
            </div>
          )}

          {/* Unique-project list */}
          <div className="fluent-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F3F2F1] text-left text-xs uppercase tracking-wide text-[#605E5C]">
                    <th className="px-3 py-2.5 font-semibold w-8"></th>
                    <th className="px-4 py-2.5 font-semibold">
                      <span className="inline-flex items-center gap-1">Project Health
                        <Info size={12} className="text-[#8A8886]" title="Calculated — schedule-based demo rules (schedule-rag-v1): expected vs actual % completion." />
                      </span>
                    </th>
                    <SortTh label="Project" k="project_name" sort={sort} dir={direction} onClick={toggleSort} />
                    <SortTh label="Platform" k="platform" sort={sort} dir={direction} onClick={toggleSort} />
                    <SortTh label="Completion" k="pct_completion" sort={sort} dir={direction} onClick={toggleSort} />
                    <th className="px-4 py-2.5 font-semibold">Last reported</th>
                    <th className="px-4 py-2.5 font-semibold">Owner</th>
                    <th className="px-4 py-2.5 font-semibold">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-[#605E5C]">No projects match your filters.</td></tr>
                  ) : data.items.map((row, i) => (
                    <Fragment key={row.project_id}>
                      <tr data-testid={`portfolio-row-${i}`}
                        className="border-t border-[#EDEBE9] hover:bg-[#F3F9FD] transition-colors">
                        <td className="px-3 py-3">
                          <button data-testid={`expand-${i}`} onClick={() => setExpanded(expanded === row.project_id ? null : row.project_id)}
                            className="text-[#605E5C] hover:text-[#0078D4] fluent-focus rounded">
                            {expanded === row.project_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <RagChip rag={row.rag} testid={`rag-${i}`}
                            isOverridden={row.is_overridden} overrideBy={row.override_by}
                            overrideAt={row.override_at} preRag={row.pre_override_rag}
                            overrideReason={row.override_reason}
                            catchupAmber={row.catchup_amber} catchupGreen={row.catchup_green} />
                          {isAdmin && row.rag === "red" && !row.is_overridden && (
                            <button data-testid={`override-btn-${i}`} onClick={() => openOverride(row)}
                              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#4338CA] hover:text-[#312E81] fluent-focus rounded">
                              <ShieldAlert size={12} /> Override
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => navigate(`/projects/${row.project_id}?as_of=${asOf}`)} className="text-left fluent-focus rounded">
                            <div className="font-semibold text-[#0078D4]">{row.project_name}</div>
                            <div className="text-[11px] text-[#605E5C]">{row.tech_stack}</div>
                          </button>
                        </td>
                        <td className="px-4 py-3">{row.platform}</td>
                        <td className="px-4 py-3 w-44">
                          <div className="flex items-center gap-2">
                            <Progress value={row.pct_completion || 0} className="h-2 flex-1" />
                            <span className="text-xs font-semibold w-9 text-right">{row.pct_completion ?? "—"}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>{row.month_label}</div>
                          {row.carried_forward && <span className="text-[10px] text-[#8A6D00] font-semibold" data-testid={`carried-${i}`}>carried forward</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div>{row.contributor_name}</div>
                          <div className="text-[11px] text-[#605E5C]">{row.contributor_id}</div>
                        </td>
                        <td className="px-4 py-3" data-testid={`trend-${i}`}>
                          <TrendCell prev={row.prev_rag} current={row.rag} trend={row.trend} />
                        </td>
                      </tr>
                      {expanded === row.project_id && (
                        <tr className="bg-[#FAFAFA]" data-testid={`expand-panel-${i}`}>
                          <td colSpan={8} className="px-6 py-4">
                            <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                              <Field label="Scope / About">{row.about}</Field>
                              <Field label="Bandwidth / Availability">{row.bandwidth}</Field>
                              <Field label="Key Achievements">{row.achievements}</Field>
                              <Field label="Challenges">{row.challenges}</Field>
                              <Field label="Dependencies / Blockers">{row.blockers}</Field>
                              <Field label="Support Ask">{row.support_required}</Field>
                              <Field label="Planned Focus Next Month">{row.next_month_focus}</Field>
                              <Field label="Schedule health rationale">{row.rag_reason}</Field>
                            </div>
                            <Button variant="outline" size="sm" className="mt-3 h-8 text-xs fluent-focus"
                              data-testid={`history-link-${i}`} onClick={() => navigate(`/projects/${row.project_id}?as_of=${asOf}`)}>
                              <History size={13} className="mr-1.5" /> Monthly History & detail
                            </Button>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[#EDEBE9]">
              <div className="flex items-center gap-2 text-xs text-[#605E5C]">
                Rows per page
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="h-8 w-[70px] text-xs" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[10, 25, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <span className="ml-2" data-testid="projects-in-view-count">{total} project(s) in view</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span data-testid="pagination-info">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" className="h-8 fluent-focus" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page"><ChevronLeft size={14} /></Button>
                <Button variant="outline" size="sm" className="h-8 fluent-focus" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page"><ChevronRight size={14} /></Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Override modal (admin) */}
      <Dialog open={!!ovTarget} onOpenChange={(o) => { if (!o) setOvTarget(null); }}>
        <DialogContent data-testid="override-modal">
          <DialogHeader>
            <DialogTitle>Override Project Health</DialogTitle>
            <DialogDescription>
              {ovTarget?.project_name} — {ovTarget?.month_label}. This sets the displayed health manually and is recorded in the audit trail. Overriding to Red is not allowed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">New health</label>
              <Select value={ovRag} onValueChange={setOvRag}>
                <SelectTrigger className="h-9 text-sm fluent-focus mt-1" data-testid="override-rag-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="amber" data-testid="override-opt-amber">Amber (At Risk)</SelectItem>
                  <SelectItem value="green" data-testid="override-opt-green">Green (On Track)</SelectItem>
                  <SelectItem value="not_rated" data-testid="override-opt-not_rated">Not Rated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">Reason (required)</label>
              <Textarea data-testid="override-reason-input" value={ovReason} onChange={(e) => setOvReason(e.target.value)}
                placeholder="Why is this override justified?" className="mt-1 text-sm fluent-focus" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-9 text-sm" onClick={() => setOvTarget(null)} data-testid="override-cancel">Cancel</Button>
            <Button className="h-9 text-sm bg-[#4338CA] hover:bg-[#312E81] text-white" onClick={submitOverride} disabled={ovSaving} data-testid="override-confirm">
              {ovSaving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Confirm override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortTh({ label, k, sort, dir, onClick }) {
  return (
    <th className="px-4 py-2.5 font-semibold">
      <button onClick={() => onClick(k)} data-testid={`sort-${k}`} className="inline-flex items-center gap-1 hover:text-[#0078D4] fluent-focus rounded">
        {label}<span className={sort === k ? "text-[#0078D4]" : "opacity-30"}>{sort === k && dir === "desc" ? "▼" : "▲"}</span>
      </button>
    </th>
  );
}

function StaticCard({ c }) {
  const Icon = c.icon;
  return (
    <div data-testid={c.tid} className="fluent-card p-3" style={{ backgroundColor: c.bg }}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">{c.title}</span>
        <Icon size={14} style={{ color: c.color }} />
      </div>
      <div className="mt-1.5 text-2xl font-semibold" style={{ color: c.color }}>{c.value ?? 0}{c.suffix || ""}</div>
    </div>
  );
}

function HealthCard({ c, value, selected, onClick }) {
  const Icon = c.icon;
  const TINT = {
    green: { bg: "#E8F5E9", border: "#107C41" },
    amber: { bg: "#FFF8E1", border: "#B38600" },
    red: { bg: "#FFEBEE", border: "#A80000" },
  };
  const t = TINT[c.health] || { bg: "#F3F2F1", border: "#8A8886" };
  return (
    <button data-testid={`kpi-card-${c.key}`} onClick={onClick}
      style={{ backgroundColor: t.bg, borderColor: t.border }}
      className={`fluent-card fluent-card-hover p-3 text-left fluent-focus border ${selected ? "ring-2 ring-[#0078D4]" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: c.color }}>{c.title}</span>
        <Icon size={14} style={{ color: c.color }} />
      </div>
      <div className="mt-1.5 text-2xl font-semibold" style={{ color: c.color }}>{value}</div>
      <div className="text-[10px] mt-0.5" style={{ color: c.color, opacity: 0.8 }}>{selected ? "Filtering ✓" : "Click to filter"}</div>
    </button>
  );
}

function TrendCell({ prev, current, trend }) {
  const cfg = {
    improved: { Icon: ArrowUp, color: "#107C41", label: "Improved" },
    deteriorated: { Icon: ArrowDown, color: "#A80000", label: "Deteriorated" },
    unchanged: { Icon: ArrowRight, color: "#605E5C", label: "Stable" },
  };
  if (!prev || trend === "none" || trend === "not_comparable") {
    return <span className="text-[11px] text-[#605E5C]" title="First reporting month — no prior health to compare">New</span>;
  }
  const c = cfg[trend] || cfg.unchanged;
  const Icon = c.Icon;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap" style={{ color: c.color }} title={`${prev} → ${current}`}>
      <Icon size={14} /> {c.label}
    </span>
  );
}
function Field({ label, children }) {
  const v = children && String(children).trim() ? children : null;
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">{label}</div>
      <div className="text-sm text-[#323130] mt-0.5">{v || <span className="text-[#A19F9D] italic">Not reported</span>}</div>
    </div>
  );
}
